import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Cancel01Icon, DiscountIcon } from 'hugeicons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SideBarContext';
import { useWindow } from '../../contexts/WindowContext';
import { SidebarIcon } from '../../assets/CustomSvgs';
import { HoverToolTip } from '../Layout/Items/HoverToolTip';
import { UserDropdown } from '../Layout/UserDropdown';
import SidebarFooter from './SidebarFooter';
import ChatList from './ChatList';
import ChatListErrorBoundary from './ChatListErrorBoundary';
import SpacesList from './SpacesList';
import ChatActions from './ChatActions';
import '../../Styles/Sidebar/index.css';
import '../../Styles/Header/Header.css';
import { useChatContext } from '../../contexts/AppDataContext';
import { TRIAL_DAYS } from '../../Data/PricingData';

const Sidebar: React.FC = React.memo(() => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar, setIsSidebarOpen } = useSidebar();
  const { isSpaceChat, setShowSalesModal } = useChatContext()
  const { windowWidth, logoImage } = useWindow();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreChats, setLoadMoreChats] = useState<(() => void) | null>(null);

  const sidebarClassName = useMemo(() => {
    const isMinimized = windowWidth > 768 && !isSidebarOpen && auth.isAuthenticated;
    return `sidebar ${!isSidebarOpen && !isMinimized ? 'collapsed' : ''} ${isSidebarOpen ? 'sidebar-locked' : ''} ${isMinimized ? 'sidebar-minimized' : ''}`;
  }, [isSidebarOpen, windowWidth, auth.isAuthenticated]);

  const handleChatRemove = useCallback((chatId: string) => {
    const chatsData = JSON.parse(localStorage.getItem('chats') || '[]');
    const currentIndex = chatsData.findIndex((chat: any) => chat.id === chatId);
    const nextChatId = currentIndex < chatsData.length - 1 ? chatsData[currentIndex + 1].id :
                       currentIndex > 0 ? chatsData[currentIndex - 1].id : 'new';

    navigate(`/${nextChatId}`, {
      replace: true,
      state: { keepSidebarOpen: true }
    });
  }, [navigate]);

  const handleActionClick = useCallback(() => {
    if (windowWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [windowWidth, setIsSidebarOpen]);

  const handlePaginationStateChange = useCallback((hasMore: boolean, isLoading: boolean, loadMore: () => void) => {
    setHasMoreChats(hasMore);
    setIsLoadingMore(isLoading);
    setLoadMoreChats(() => loadMore);
  }, []);

  // Intersection Observer for pagination
  useEffect(() => {
    if (!sentinelRef.current || !loadMoreChats) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && hasMoreChats && !isLoadingMore) {
            loadMoreChats();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [loadMoreChats, hasMoreChats, isLoadingMore]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (windowWidth <= 768) {
        const sidebar = document.querySelector('.sidebar');
        const chatHeader = document.querySelector('.chat-header');
        const target = event.target as Node;
        const targetEl = target instanceof Element ? target : target.parentElement;
        const isInPortal = targetEl?.closest('[data-radix-popper-content-wrapper]');
        
        if (sidebar && 
            chatHeader && 
            !sidebar.contains(target) && 
            !chatHeader.contains(target) &&
            !isInPortal) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsSidebarOpen, windowWidth]);

  const renderUpgradeButton = () => {
    // Hidden for Ultimate, the top plan
    if (auth?.subscription?.plan === 'ultimate') return null;
    return (
      <button 
        onClick={() => {
          setShowSalesModal(true);
        }}
        className="plan-upgrade-button"
        aria-label="Upgrade Plan"
      >
        <div className='feature-icon'>
          <DiscountIcon size={22} />
        </div>
        <div className='upgrade-main'>
          <span className='upgrade-main-text'>
            Upgrade Plan
          </span>
          <span className='upgrade-description'>
            {auth?.subscription?.plan === 'premium' ? 'Get Spaces With Ultimate' : `Try Free for ${TRIAL_DAYS} Days`}
          </span>
        </div>
      </button>
    );
  };

  const isMinimized = windowWidth > 768 && !isSidebarOpen && auth.isAuthenticated;

  return auth.isAuthenticated ? (
    <aside 
      className={sidebarClassName}
    >
      {isMinimized ? (
        <>
          <div className='sidebar-minimized-main'>
            {isSpaceChat ? (
              <NavLink to="/" className="sidebar-minimized-logo-link">
                <img 
                  src={logoImage} 
                  alt="StudyBuddy Logo" 
                  className="sidebar-minimized-logo"
                />
              </NavLink>
            ) : (
              <button onClick={toggleSidebar}>
                <SidebarIcon isOpen={isSidebarOpen} />
                <HoverToolTip
                  text="Toggle Sidebar"
                  position="right"
                />
              </button>
            )}
          </div>
          <div className='sidebar-minimized-main'>
            <ChatActions isMinimized={true} onActionClick={handleActionClick} />
          </div>
          <div className='sidebar-minimized-bottom'>
            <UserDropdown />
          </div>
        </>
      ) : (
        <>
          <div className="sidebar-header">
            <div className="sidebar-header-top">
            {windowWidth > 768 ? (
              <button
                className="sidebar-toggle-btn"
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
              >
                <SidebarIcon width={20} height={20} color="var(--text-primary)" />
              </button>
            ) : (
              <NavLink to="/" className="sidebar-minimized-logo-link">
                <img
                  src={logoImage}
                  alt="StudyBuddy Logo"
                  className="sidebar-minimized-logo"
                />
              </NavLink>
            )}

              {windowWidth < 768 && (
                <button onClick={toggleSidebar} className="sidebar-toggle-btn">
                  <Cancel01Icon size={18} />
                </button>
              )}
            </div>
            <ChatActions onActionClick={handleActionClick} />
          </div>
          
          {isSidebarOpen && (
            <>
              <div className="sidebar-content">
                <nav className="sidebar-nav">
                  <div className="scrollable-section">
                    <div className="sidebar-section">
                      <SpacesList onSpaceClick={handleActionClick} />
                    </div>
                    
                    <ChatListErrorBoundary>
                      <ChatList 
                        isSidebarOpen={isSidebarOpen} 
                        onChatRemove={handleChatRemove}
                        onPaginationStateChange={handlePaginationStateChange}
                      />
                    </ChatListErrorBoundary>
                    
                    {hasMoreChats && (
                      <div 
                        ref={sentinelRef}
                        className="pagination-sentinel"
                        style={{ height: '20px', margin: '10px 0' }}
                      />
                    )}
                  </div>
                </nav>
              </div>

              <div className='sidebar-upgrade-section'>
                {renderUpgradeButton()}
              </div>
              
              <SidebarFooter />
            </>
          )}
        </>
      )}
    </aside>
  ) : null;
});

export default Sidebar;
